import { MeshProps, useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Color,
  DoubleSide,
  IUniform,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Uniform,
} from 'three';

import fragmentShader from './shaders/fragment.glsl';
import vertexShader from './shaders/vertex.glsl';

export interface IndicationRingsProps extends MeshProps {
  color?: Color;
  ringsAmount?: number;
  thickness?: number;
  tempo?: number;
}

export interface IndicationRingsRef {
  mesh: Mesh;
  opacity: IUniform<number>;
}

const IndicationRings = forwardRef<IndicationRingsRef, IndicationRingsProps>(
  (
    {
      geometry = new PlaneGeometry(10, 10, 64, 64),
      color = new Color(0xffffff),
      ringsAmount = 4.0,
      thickness = 0.02,
      tempo = 2.0,
      ...props
    },
    ref
  ) => {
    const meshRef = useRef<Mesh>(null!);
    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        side: DoubleSide,
        transparent: true,
        uniforms: {
          uColor: new Uniform(color),
          uTime: { value: 0.0 },
          uRingsAmount: { value: ringsAmount },
          uThickness: { value: thickness },
          uTempo: { value: tempo },
          uVisibility: { value: 0.0 },
        },
      })
    );

    useFrame(({ clock }) => {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    });

    useImperativeHandle(ref, () => ({
      mesh: meshRef.current,
      opacity: materialRef.current.uniforms.uVisibility,
    }));

    return (
      <mesh {...props} geometry={geometry} ref={meshRef}>
        <primitive object={materialRef.current} attach="material" />
      </mesh>
    );
  }
);

IndicationRings.displayName = 'IndicationRings';
export default IndicationRings;
